import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Link2, Link2Off, FileSpreadsheet, RefreshCw, Settings, Check,
  AlertTriangle, ChevronRight, Eye, Columns, Table2, ShieldCheck, Loader2,
  ExternalLink, X, Info
} from 'lucide-react';

const STATUS_MAP = {
  disconnected: { label: 'Desconectado', color: 'bg-gray-100 text-gray-600', icon: Link2Off },
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
  connected: { label: 'Conectado', color: 'bg-emerald-100 text-emerald-700', icon: Check },
  expired: { label: 'Expirado', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const DATA_TYPES = [
  { value: 'facturas', label: 'Facturas' },
  { value: 'cobros', label: 'Cobros' },
  { value: 'clientes', label: 'Clientes' },
  { value: 'vendedores', label: 'Vendedores' },
  { value: 'empresas', label: 'Empresas' },
  { value: 'catalogos', label: 'Catálogos' },
  { value: 'datos_complementarios', label: 'Datos complementarios' },
  { value: 'ignorar', label: 'Ignorar' },
];

const AMOUNT_TREATMENTS = [
  { value: 'line_amount', label: 'Cada línea tiene su importe' },
  { value: 'header_only', label: 'Total solo en primera línea' },
  { value: 'repeated_total', label: 'Total repetido en todas las líneas' },
  { value: 'specific_column', label: 'Obtener de columna específica' },
  { value: 'custom', label: 'Regla personalizada' },
];

const INVOICE_COLUMNS = [
  { key: 'invoice_number', label: 'No. Factura', required: true },
  { key: 'invoice_date', label: 'Fecha', required: true },
  { key: 'client_name', label: 'Cliente', required: true },
  { key: 'company_name', label: 'Empresa', required: false },
  { key: 'seller_name', label: 'Vendedor', required: false },
  { key: 'responsible_name', label: 'Encargado', required: false },
  { key: 'concept', label: 'Concepto', required: false },
  { key: 'concept_key', label: 'Clave', required: false },
  { key: 'description', label: 'Descripción / Explicación', required: false },
  { key: 'currency', label: 'Moneda', required: false },
  { key: 'subtotal', label: 'Monto sin IVA', required: false },
  { key: 'total_amount', label: 'Monto con IVA', required: true },
  { key: 'collected_amount', label: 'Cobrado', required: false },
  { key: 'invoice_status', label: 'Status', required: false },
  { key: 'payment_method', label: 'Método de pago', required: false },
  { key: 'invoice_type', label: 'Tipo (iguala/one shot)', required: false },
  { key: 'payment_date', label: 'Fecha de pago', required: false },
  { key: 'due_date', label: 'Promesa de pago', required: false },
  { key: 'notes', label: 'Notas', required: false },
  { key: 'entry_date', label: 'Fecha de ingreso', required: false },
  { key: 'estimated_payment_date', label: 'Estimado de pago', required: false },
];

// ============ Connection Card ============
function ConnectionCard({ status, onConnect, onDisconnect, loading }) {
  const statusInfo = STATUS_MAP[status?.status] || STATUS_MAP.disconnected;
  const StatusIcon = statusInfo.icon;
  const isConnected = status?.status === 'connected';
  const isConfigured = status?.configured;

  return (
    <Card data-testid="google-connection-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
            <div>
              <CardTitle className="text-base">Google Sheets</CardTitle>
              <CardDescription className="text-xs">
                {isConnected ? `Conectado como ${status.google_email}` : 'Fuente de datos para facturación'}
              </CardDescription>
            </div>
          </div>
          <Badge className={statusInfo.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {!isConfigured && !isConnected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <p className="font-medium">Configuración pendiente</p>
                <p className="mt-0.5">Las credenciales de Google OAuth (GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET) no están configuradas. Contacte al administrador del sistema.</p>
              </div>
            </div>
          </div>
        )}

        {isConnected && status.selected_file_name && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-muted/50 rounded-lg">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium">{status.selected_file_name}</span>
          </div>
        )}

        {status?.error_message && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <p className="text-xs text-red-700">{status.error_message}</p>
          </div>
        )}

        <div className="flex gap-2">
          {isConnected ? (
            <Button variant="outline" size="sm" onClick={onDisconnect} disabled={loading}
              className="text-red-600 hover:text-red-700 gap-1.5" data-testid="disconnect-google-btn">
              <Link2Off className="w-3.5 h-3.5" />
              Desconectar
            </Button>
          ) : (
            <Button size="sm" onClick={onConnect} disabled={loading || !isConfigured}
              className="gap-1.5" data-testid="connect-google-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Conectar con Google
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============ File Selector ============
function FileSelector({ files, selectedFileId, onSelect, onLoadMore, nextPageToken, loading }) {
  if (loading && !files.length) {
    return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;
  }

  return (
    <div className="space-y-2" data-testid="file-selector">
      {files.map(f => (
        <div key={f.id}
          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
            f.id === selectedFileId ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onClick={() => onSelect(f.id)}
          data-testid={`file-item-${f.id}`}
        >
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-sm font-medium">{f.name}</p>
              <p className="text-xs text-muted-foreground">
                {f.owner} · Modificado: {new Date(f.modified_at).toLocaleDateString('es-MX')}
              </p>
            </div>
          </div>
          {f.id === selectedFileId && <Check className="w-5 h-5 text-primary" />}
        </div>
      ))}
      {nextPageToken && (
        <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loading} className="w-full">
          Cargar más archivos
        </Button>
      )}
      {!files.length && !loading && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No se encontraron archivos de Google Sheets
        </p>
      )}
    </div>
  );
}

// ============ Sheet Config Card ============
function SheetConfigCard({ sheet, config, onToggle, onConfigure }) {
  const isActive = config?.active ?? false;

  return (
    <div className={`border rounded-lg p-3 transition-colors ${isActive ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Table2 className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
          <div>
            <p className="text-sm font-medium">{sheet.title}</p>
            <p className="text-xs text-muted-foreground">
              {sheet.row_count} filas · {sheet.column_count} columnas
              {sheet.has_merges && (
                <span className="text-amber-600 ml-2">
                  <AlertTriangle className="w-3 h-3 inline" /> {sheet.merge_count} celdas combinadas
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && config?.data_type && (
            <Badge variant="outline" className="text-[10px]">
              {DATA_TYPES.find(d => d.value === config.data_type)?.label || config.data_type}
            </Badge>
          )}
          {isActive && (
            <Button variant="outline" size="sm" onClick={() => onConfigure(sheet)} className="gap-1 text-xs h-7">
              <Columns className="w-3 h-3" /> Mapear
            </Button>
          )}
          <Switch checked={isActive} onCheckedChange={() => onToggle(sheet)} />
        </div>
      </div>
    </div>
  );
}

// ============ Column Mapping Dialog ============
function ColumnMappingDialog({ open, onClose, sheet, headers, config, onSave }) {
  const [mapping, setMapping] = useState(config?.column_mapping || {});
  const [dataType, setDataType] = useState(config?.data_type || 'facturas');
  const [headerRow, setHeaderRow] = useState(config?.header_row || 1);
  const [dataStartRow, setDataStartRow] = useState(config?.data_start_row || 2);
  const [amountTreatment, setAmountTreatment] = useState(config?.amount_treatment || 'line_amount');

  useEffect(() => {
    if (config) {
      setMapping(config.column_mapping || {});
      setDataType(config.data_type || 'facturas');
      setHeaderRow(config.header_row || 1);
      setDataStartRow(config.data_start_row || 2);
      setAmountTreatment(config.amount_treatment || 'line_amount');
    }
  }, [config]);

  const handleSave = () => {
    onSave({
      ...config,
      data_type: dataType,
      header_row: headerRow,
      data_start_row: dataStartRow,
      column_mapping: mapping,
      amount_treatment: amountTreatment,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Columns className="w-5 h-5 text-primary" />
            Configurar: {sheet?.title}
          </DialogTitle>
          <DialogDescription>Define el tipo de datos, filas de inicio y mapeo de columnas</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic settings */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo de información</Label>
              <Select value={dataType} onValueChange={setDataType}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent position="popper">
                  {DATA_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tratamiento de importes</Label>
              <Select value={amountTreatment} onValueChange={setAmountTreatment}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent position="popper">
                  {AMOUNT_TREATMENTS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fila de encabezados</Label>
              <Input type="number" value={headerRow} onChange={e => setHeaderRow(Number(e.target.value))} className="h-8 text-xs mt-1" min={1} />
            </div>
            <div>
              <Label className="text-xs">Primera fila de datos</Label>
              <Input type="number" value={dataStartRow} onChange={e => setDataStartRow(Number(e.target.value))} className="h-8 text-xs mt-1" min={2} />
            </div>
          </div>

          <Separator />

          {/* Column mapping */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Mapeo de columnas</h4>
            <p className="text-xs text-muted-foreground mb-3">Selecciona qué columna del Sheet corresponde a cada campo</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {INVOICE_COLUMNS.map(col => (
                <div key={col.key} className="flex items-center gap-2">
                  <div className="w-40 flex-shrink-0">
                    <span className="text-xs">{col.label}</span>
                    {col.required && <span className="text-red-500 ml-0.5 text-[10px]">*</span>}
                  </div>
                  <Select value={mapping[col.key] || '_none'} onValueChange={v => setMapping(m => ({...m, [col.key]: v === '_none' ? undefined : v}))}>
                    <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Sin mapear" /></SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="_none">— Sin mapear —</SelectItem>
                      {(headers || []).map((h, i) => (
                        <SelectItem key={i} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} className="gap-1.5">
            <Check className="w-3.5 h-3.5" /> Guardar configuración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN COMPONENT ============
export default function FacturacionConfig({ onBack }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetConfigs, setSheetConfigs] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [mappingSheet, setMappingSheet] = useState(null);
  const [mappingHeaders, setMappingHeaders] = useState([]);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [step, setStep] = useState('connection'); // connection, files, sheets

  const isAdmin = user?.role && ['super_admin', 'admin', 'admin_cliente'].includes(user.role);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/google/status');
      setConnectionStatus(res.data);
      if (res.data.status === 'connected') {
        if (res.data.selected_file_id) {
          setSelectedFileId(res.data.selected_file_id);
          setStep('sheets');
        } else {
          setStep('files');
        }
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await api.post('/google/connect');
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al conectar con Google';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/google/disconnect');
      setConnectionStatus({ ...connectionStatus, status: 'disconnected', google_email: null });
      setFiles([]);
      setSheets([]);
      setStep('connection');
      setDisconnectDialogOpen(false);
      toast.success('Google desconectado');
    } catch {
      toast.error('Error al desconectar');
    }
  };

  const loadFiles = async (pageToken = null) => {
    setLoadingFiles(true);
    try {
      const params = pageToken ? { page_token: pageToken } : {};
      const res = await api.get('/google/files', { params });
      if (pageToken) {
        setFiles(prev => [...prev, ...res.data.files]);
      } else {
        setFiles(res.data.files);
      }
      setNextPageToken(res.data.next_page_token);
    } catch {
      toast.error('Error al cargar archivos');
    } finally {
      setLoadingFiles(false);
    }
  };

  const selectFile = async (fileId) => {
    try {
      const res = await api.post(`/google/files/${fileId}/select`);
      setSelectedFileId(fileId);
      setConnectionStatus(prev => ({ ...prev, selected_file_id: fileId, selected_file_name: res.data.file_name }));
      toast.success(`Archivo seleccionado: ${res.data.file_name}`);
      loadSheets(fileId);
      setStep('sheets');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al seleccionar archivo');
    }
  };

  const loadSheets = async (fileId) => {
    setLoadingSheets(true);
    try {
      const [sheetsRes, configsRes] = await Promise.all([
        api.get(`/google/files/${fileId}/sheets`),
        api.get('/google/sheet-configs'),
      ]);
      setSheets(sheetsRes.data);
      setSheetConfigs(configsRes.data);
    } catch {
      toast.error('Error al cargar pestañas');
    } finally {
      setLoadingSheets(false);
    }
  };

  const toggleSheet = async (sheet) => {
    const existing = sheetConfigs.find(c => c.sheet_name === sheet.title);
    const newConfig = {
      file_id: selectedFileId,
      sheet_name: sheet.title,
      active: existing ? !existing.active : true,
      data_type: existing?.data_type || 'facturas',
      header_row: existing?.header_row || 1,
      data_start_row: existing?.data_start_row || 2,
      column_mapping: existing?.column_mapping || {},
      amount_treatment: existing?.amount_treatment || 'line_amount',
      id: existing?.id,
    };

    try {
      const res = await api.post('/google/sheet-configs', newConfig);
      setSheetConfigs(prev => {
        const idx = prev.findIndex(c => c.sheet_name === sheet.title);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = res.data;
          return updated;
        }
        return [...prev, res.data];
      });
    } catch {
      toast.error('Error al actualizar configuración');
    }
  };

  const openMapping = async (sheet) => {
    setMappingSheet(sheet);
    if (connectionStatus?.status === 'connected' && selectedFileId) {
      try {
        const res = await api.get(`/google/files/${selectedFileId}/sheets/${encodeURIComponent(sheet.title)}/preview`, {
          params: { header_row: 1, data_start_row: 2, max_rows: 5 }
        });
        setMappingHeaders(res.data.headers || []);
      } catch {
        setMappingHeaders([]);
      }
    }
    setMappingDialogOpen(true);
  };

  const saveMapping = async (config) => {
    try {
      const res = await api.post('/google/sheet-configs', config);
      setSheetConfigs(prev => {
        const idx = prev.findIndex(c => c.sheet_name === config.sheet_name);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = res.data;
          return updated;
        }
        return [...prev, res.data];
      });
      toast.success('Configuración guardada');
    } catch {
      toast.error('Error al guardar configuración');
    }
  };

  // Check for OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      toast.success(`Google conectado como ${params.get('email') || ''}`);
      loadStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_error')) {
      const errors = {
        invalid_state: 'Error de seguridad: estado inválido. Intente de nuevo.',
        exchange_failed: 'Error al intercambiar tokens con Google.',
      };
      toast.error(errors[params.get('google_error')] || 'Error al conectar con Google');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-sm">Solo los administradores pueden configurar la fuente de datos.</p>
      </div>
    );
  }

  if (loading && !connectionStatus) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="facturacion-config-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8" data-testid="config-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Configurar fuente de datos
          </h2>
          <p className="text-xs text-muted-foreground">Conecta Google Sheets para sincronizar facturas</p>
        </div>
      </div>

      {/* Step 1: Connection */}
      <ConnectionCard
        status={connectionStatus}
        onConnect={handleConnect}
        onDisconnect={() => setDisconnectDialogOpen(true)}
        loading={loading}
      />

      {/* Step 2: File Selection (only when connected) */}
      {connectionStatus?.status === 'connected' && step === 'files' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Seleccionar archivo</CardTitle>
              <Button variant="outline" size="sm" onClick={() => loadFiles()} disabled={loadingFiles} className="gap-1.5 text-xs">
                <RefreshCw className={`w-3 h-3 ${loadingFiles ? 'animate-spin' : ''}`} /> Recargar
              </Button>
            </div>
            <CardDescription className="text-xs">Elige el Google Sheet que contiene tus datos de facturación</CardDescription>
          </CardHeader>
          <CardContent>
            <FileSelector
              files={files}
              selectedFileId={selectedFileId}
              onSelect={selectFile}
              onLoadMore={() => loadFiles(nextPageToken)}
              nextPageToken={nextPageToken}
              loading={loadingFiles}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Sheet Configuration (when file is selected) */}
      {connectionStatus?.status === 'connected' && step === 'sheets' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Configurar pestañas</CardTitle>
                <CardDescription className="text-xs">
                  Activa las pestañas que alimentarán el dashboard y configura el mapeo
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setStep('files'); loadFiles(); }} className="gap-1 text-xs">
                  <FileSpreadsheet className="w-3 h-3" /> Cambiar archivo
                </Button>
                <Button variant="outline" size="sm" onClick={() => loadSheets(selectedFileId)} disabled={loadingSheets} className="gap-1 text-xs">
                  <RefreshCw className={`w-3 h-3 ${loadingSheets ? 'animate-spin' : ''}`} /> Recargar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSheets ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
            ) : (
              <div className="space-y-2">
                {sheets.map(s => (
                  <SheetConfigCard
                    key={s.sheet_id}
                    sheet={s}
                    config={sheetConfigs.find(c => c.sheet_name === s.title)}
                    onToggle={toggleSheet}
                    onConfigure={openMapping}
                  />
                ))}
                {!sheets.length && (
                  <p className="text-sm text-muted-foreground text-center py-4">No se encontraron pestañas</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Not connected: show placeholder steps */}
      {connectionStatus?.status !== 'connected' && (
        <div className="space-y-3 opacity-50 pointer-events-none">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">2</div>
              <div>
                <p className="text-sm font-medium">Seleccionar archivo de Google Sheets</p>
                <p className="text-xs text-muted-foreground">Disponible después de conectar con Google</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">3</div>
              <div>
                <p className="text-sm font-medium">Configurar pestañas y mapeo de columnas</p>
                <p className="text-xs text-muted-foreground">Disponible después de seleccionar un archivo</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">4</div>
              <div>
                <p className="text-sm font-medium">Sincronizar datos</p>
                <p className="text-xs text-muted-foreground">Disponible después de configurar pestañas</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Desconectar Google?</DialogTitle>
            <DialogDescription>
              Se revocarán los permisos de acceso. Los datos ya sincronizados se conservarán, pero no podrás sincronizar nuevos datos hasta reconectar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDisconnect} className="gap-1.5">
              <Link2Off className="w-3.5 h-3.5" /> Desconectar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Mapping Dialog */}
      <ColumnMappingDialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        sheet={mappingSheet}
        headers={mappingHeaders}
        config={sheetConfigs.find(c => c.sheet_name === mappingSheet?.title)}
        onSave={saveMapping}
      />
    </div>
  );
}
